'use client';

import HistoryTable from '@/features/roadmap/components/HistoryTable/HistoryTable';
import { getChangedTimeText } from '@/utils/string';
import { Tooltip } from '@andritzot/metris-web-ui/data-display/tooltip';
import { IconButton } from '@andritzot/metris-web-ui/inputs/icon-button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Popover } from '@andritzot/metris-web-ui/utils/popover';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import HistoryIcon from '@mui/icons-material/History';
import LockOutlined from '@mui/icons-material/LockOutlined';
import { type MouseEvent, type ReactNode, useState } from 'react';

interface HistoryConfig {
  roadmapId: number;
  field: string;
  changesCount: number;
}

interface FieldActionsWrapperProps {
  children: ReactNode;
  locked?: boolean;
  copyValue?: string;
  lockedTooltip?: string;
  history?: HistoryConfig;
}

export function FieldActionsWrapper({
  children,
  locked,
  copyValue,
  lockedTooltip,
  history,
}: Readonly<FieldActionsWrapperProps>) {
  const [copied, setCopied] = useState(false);
  const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleCopy = async () => {
    if (!copyValue) return;
    await navigator.clipboard.writeText(copyValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenHistory = (event: MouseEvent<HTMLButtonElement>) => {
    setHistoryAnchorEl(event.currentTarget);
  };

  const showActions = locked || copyValue != null || history != null;

  if (!showActions) return <>{children}</>;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, pt: 0.25, mr: 0.5 }}>
        {copyValue != null && (
          <Tooltip title={copied ? 'Copied!' : 'Copy'}>
            <IconButton size="small" onClick={() => void handleCopy()} color={copied ? 'success' : 'primary'} disabled={!copyValue || locked}>
              {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        {history != null && (
          <>
            <Tooltip title={`Changed ${getChangedTimeText(history.changesCount)}`}>
              <IconButton size="small" onClick={handleOpenHistory} color="primary">
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {historyAnchorEl && (
              <Popover
                open
                anchorEl={historyAnchorEl}
                onClose={() => setHistoryAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <HistoryTable roadmapId={history.roadmapId} field={history.field} />
              </Popover>
            )}
          </>
        )}
        {locked && (
          <Tooltip title={lockedTooltip ?? 'This field is locked'}>
            <LockOutlined fontSize="small" color="disabled" />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
